import stripTags from 'underscore.string/stripTags'
import Storage from 'shared/helpers/storage'
import environment from 'shared/environment'
import {
  is,
  anyPass,
  isNil,
  isEmpty,
  values,
  pick,
  mapObjIndexed,
} from 'ramda'

const impAuthIdKey = environment.impAuthIdKey

const { urls } = environment

// Map cryptic query param names to friendlier ones
const urlParamNamesMap = {
  r: 'redirectLocation',
  [impAuthIdKey]: 'impersonationAuthId',
}

function getEnvironment () {
  return environment
}

export {getEnvironment as environment}

/**
 * Get an identifier for the current environment.
 */
export function currentEnvironment () {
  return environment.hostToEnv[window.location.host] || environment.ENVS.DEV
}

export function appUrls () {
  return values(pick(environment.appIds, urls))
}

/*
TODO:react-refactor had to bring over a bunch of gnarly URL reading and manipulation
stuff that I didn't have time to untangle. Want to seriously go through all of this stuff
Among other things:
  * Minimize, perhaps to zero, the window.location stuff happening in here.
  * In createAccessor(), looping through URLs and testing with onPage() seems pointless
  when `baseUrl` has been passed. Also see if the onPage() test can be eliminated
  and each site can just have its own createAccessor() that calls this one with the appropriate URL
*/

const onPage = absPath => {
  const matches = window.location.pathname.match(/(\/.*\/)/)

  return matches && matches[0] === absPath
}

export const onLoginPage = () => onPage(urls.login)
export const onRegisterPage = () => onPage(urls.register)
export const onAdminPage = () => onPage(urls.admin)
export const onResponderPage = () => onPage(urls.responder)
export const onAnswerPage = () => onPage(urls.answer)

export const onImpersonatablePage = anyPass([
  onResponderPage,
  onAnswerPage,
])

// unique accessors for every 'app'
// Provide 'baseUrl' to override current app
export function createAccessor (acc, baseUrl) {
  const replace = url => url.replace(/\//g, '')
  const validBaseUrl = baseUrl == null || typeof baseUrl === 'string'

  if (!validBaseUrl) {
    throw new Error('Invalid baseUrl in createAccessor')
  }

  let matched

  if (baseUrl) {
    matched = baseUrl
  } else {
    appUrls().some(url => {
      if (onPage(url)) {
        matched = url
      }

      return matched
    })
  }

  return matched ? `${acc}-${replace(matched)}` : acc
}

// this is the naming convention used in old answer, but refers to the top level path e.g. /answer/
export function currentUrl () {
  return window.location.pathname || // not supported on some browsers
    document.location.toString().replace(/^([^:]*:\/\/[^/]*)(\/[^/]*\/)(.*)$/, '$2')
}

export function currentPath (options = {}) {
  const path = window.location.hash.replace(/\?.*/, '') // rm params
  return path.replace('#', '') // rm hash
}

function getParamsString (url) {
  // should equal blah=boo&hi=you (etc)
  url = url || window.location.href
  // not using window.location.search because window.location.search === ''
  // if hash comes before search #hi?foo=bar
  const paramsStr = url.replace(`${currentPath()}`, '') // rm hash

  return paramsStr.replace(/(^.*\?)|(^.*)/, '') // rm e'rything but params
}

// get url parameters
function getParams (url) {
  const paramObj = {}
  const str = getParamsString(url)

  str.split('&')
  .forEach(pair => {
    let [key, value] = pair.split('=')

    key = urlParamNamesMap[key] || key
    paramObj[key] = value
  })

  return paramObj
}

// go to a fully qualified url
// this will also work for Backbone routes, but it's recommended to use
//   Backbone.history.navigate for that instead
export function goTo (url, {
  params = getParams(),
  newWindow = false,
} = {}) {
  const authId = params.impersonationAuthId
  const urlContainsAuthId = url.indexOf(impAuthIdKey) > -1

  if (authId && !urlContainsAuthId && !onPage(urls.login)) {
    url = url.replace(/\?|$/, `?${impAuthIdKey}=${authId}&`)
    url = url.replace(/&#/, '') // remove trailing & if it is indeed trailing
    // making the url w3c compliant (query params then hash) breaks IE11 when impersonating
    //   a user and trying to answer from the Responder app
    // Haven't been able to figure out why this is... Feel free to jump down the rabbit hole,
    //   I'm very curious
    // url = url.replace /^(.*)(#.*)(\?.*)$/, (full, $1, $2, $3) ->
    //   // switch the order of params & backbone routes to be w3c compliant
    //   "#{$1||''}#{$3||''}#{$2||''}"
  }

  if (newWindow) {
    window.open(url)
  } else {
    // TODO - check current url?
    // if window.location.pathname != url
    window.location.href = url
  }
}

// unsets the redirect location & redirects to that location (if stored)
// returns the redirect location, and null if none is stored
export function goToRedirectLocation () {
  const path = getParams().redirectLocation

  if (path) {
    goTo(decodeURIComponent(path))
  }
  return path
}


export function setFlashNotice (message, type) {
  if (!(message && type)) return

  const notice = {message, type}

  Storage.setItem(environment.noticeAccessor, JSON.stringify(notice))
}

export function makePageTitle ({
  currentTitle = '',
  newTitle,
}) {
  let titlePrefix = currentTitle
  const pipeIndex = titlePrefix.indexOf('|')

  if (pipeIndex !== -1) {
    titlePrefix = titlePrefix.slice(0, pipeIndex)
  }

  return `${titlePrefix.trim()} | ${stripTags(newTitle).trim()}`
}

// ACCEPTS: ([origin], [directoryPath], [backboneRoute])
// if no args are provided, a link to the host root will be created
// example args
//   origin - "https://www.incrowdanswers.com"
//            (optional, defaults to current origin)
//   directoryPath - "/login/" (optional, needs leading & trailing slash)
//   hashRoute - "do/something" (optional, no hash needed but allowed)
//   params - {paramName: 'value', name: 345}
export function assembleFullUrl (
  origin = '',
  directoryPath = '/',
  hashRoute = '',
  params,
  enc = true
) {
  if (is(String, origin) && origin.indexOf(':') === -1) {
    enc = is(Boolean, params) ? params : true
    params = is(Object, hashRoute) ? hashRoute : {}
    hashRoute = directoryPath !== '/' ? directoryPath : ''
    directoryPath = origin !== '' ? origin : '/'
    origin = `${window.location.protocol}//${window.location.host}`
  }

  if (!isNil(origin) && !isNil(directoryPath) && !isNil(hashRoute)) {
    let url = `${origin}${directoryPath}`

    if (!isEmpty(params)) {
      url += `?${mapObjIndexed(
        (value, name) => `${name}=${enc ? encodeURIComponent(value) : value}`,
        params
      ).join('&')}`
    }

    if (hashRoute) {
      if (String(hashRoute).indexOf('#') === -1) {
        url += `#${hashRoute}`
      } else {
        url += `${hashRoute}`
      }
    }

    return url
  }

  return ''
}

export const impersonationAuthId = () =>
  (onImpersonatablePage() && getParams()[urlParamNamesMap[impAuthIdKey]]) || null

export const isImpersonating = () => Boolean(impersonationAuthId())

export const getImpersonationParams = () => {
  const params = {}

  if (isImpersonating()) {
    params[impAuthIdKey] = impersonationAuthId()
  }

  return params
}
